import React, { useState } from 'react'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import ArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import ArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import CustomTooltip from '../tooltip'
import { makeStyles } from '@material-ui/core/styles'
// NOTE: for abl-table `getObjValueByKey` should be used every time u need to get a value from an data object.
import { getObjValueByKey, sort } from '../../../../../utils'
import classNames from 'classnames'
import { Checkbox } from '../'
import { cloneDeep } from 'lodash'

const useStyles = makeStyles(theme => ({
    wrapper: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        border: theme.border,
        borderRadius: 10,
        maxHeight: '100%',
        position: 'relative',
        overflow: 'hidden'
    },
    table: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
    },
    headerRow: {
        height: 48,
        minHeight: 48,
        backgroundColor: `${theme.background} !important`,
        borderTopLeftRadius: 10,
        borderTopRightRadius: 10
    },
    bodyWrapper: {
        overflow: 'auto',
        maxHeight: '100%',
        borderBottomLeftRadius: 10,
        borderBottomRightRadius: 10,
        marginTop: 48,
        overflowY: 'scroll'
    },
    body: {
        /* '&:nth-of-type(odd)': {
            backgroundColor: theme.background
        },
        '&:nth-of-type(even)': {
            backgroundColor: 'black !important'
        } */
    },
    headerCell: {
        alignItems: 'center',
        color: `${theme.textSecondary} !important`,
        paddingTop: 0,
        paddingBottom: 0
    },
    headerMultiSelectCell: {
        flex: '0 0 6%'
    },
    headerCellSortable: {
        '&:hover': {
            color: `${theme.textPrimary} !important`,
            fontWeight: '700 !important'
        }
    },
    cell: {
        flex: 1,
        cursor: 'default',
        color: theme.textPrimary,
        display: 'flex',
        borderBottom: 'none',
        wordBreak: 'break-word'
        // padding: '0 40px 0 24px'
    },
    multiSelectCell: {
        flex: '0 0 6%',
        cursor: 'default',
        color: theme.textPrimary,
        borderBottom: 'none',
        padding: '0 4px 0 24px'
    },
    row: {
        display: 'flex',
        alignItems: 'center',
        // height: 48,
        minHeight: 48,
        '&:nth-of-type(odd)': {
            backgroundColor: theme.background
        },
        '&:nth-of-type(even)': {
            backgroundColor: theme.tertiary
        }
    },
    rowAutoHeight: {
        height: 'auto'
    },
    ellipsis: {
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        whiteSpace: 'nowrap'
    },
    tableHeaderWrapper: {
        position: 'absolute',
        width: '100%',
        // overflowY: 'scroll',
        background: theme.background,
        zIndex: 10,
        // height: 48,
        borderBottom: theme.border,
        '&::-webkit-scrollbar': {
            background: 'transparent',
            height: 0
        }
    },
    tableHead: {
        width: 'calc(100% - 15px)' // space margin for scrollbar in body wrapper
    },
    cellAlignCenter: {
        justifyContent: 'center'
    },
    headerLabel: {
        fontWeight: 'bold'
    },
    headerLabelSortbale: {
        cursor: 'pointer'
    },
    headerCellsWrap: {},
    loaderContainer: {},
    loader: {}
}))

function CustomTable(props) {
    const classes = useStyles()

    const [sortBy, setSortBy] = useState(null)

    let list = props.rows
    if (sortBy && props.rows && props.rows.length) {
        const desc = !sortBy.dataType || sortBy.dataType === 'string' ? !sortBy.desc : sortBy.desc
        list = sort(props.rows, sortBy.key, sortBy.dataType, !desc)
    }

    const uniqueKey = props.dataUniqueKey || 'id'

    const shouldRenderBodyRows = list && list.length > 0

    const getRowKey = (rowData, index) => {
        const { getUniqueRowKey } = props
        if (typeof getUniqueRowKey === 'function') {
            return getUniqueRowKey(rowData, index)
        }
        return getObjValueByKey(rowData, uniqueKey) || index // TODO: check it
    }

    const renderBodyRow = (rowData, index) => {
        const { onRowClick, useCustomRowRenderer, customRowRenderer, classes,
            columns, customRenderer, rowAutoHeight, rows, getRowClasses, multiselect,
            select, selected } = props
        const rowKey = getRowKey(rowData, index)
        // todo: consider moving code related to nested table to another component
        if (useCustomRowRenderer) {
            return customRowRenderer({
                rowKey,
                index,
                tableClasses: classes,
                rowData,
                columns,
                uniqueKey: uniqueKey,
                currentTreeBranch: rows
            })
        }

        const rowClasses = typeof getRowClasses === 'function' ? getRowClasses(rowData, index) : undefined
        return (
            <CustomTableRow
                key={rowKey}
                classes={rowClasses}
                autoHeight={rowAutoHeight}
                item={rowData}
                columns={columns}
                customRenderer={customRenderer}
                onRowClick={onRowClick}
                multiselect={multiselect}
                uniqueKey={rowKey}
                select={select}
                selected={selected}
            />
        )
    }

    const bodyRows = (!shouldRenderBodyRows) ? null : list.map(renderBodyRow)

    let body = (
        <Table>
            <TableBody className={classes.body}>{bodyRows}</TableBody>
        </Table>
    )

    body = (props.wrapBody) ? props.wrapBody(body) : body

    const onHeaderColumnClick = (column) => {
        if (!column.sortable) {
            return
        }

        if (!sortBy || sortBy.key !== column.key) {
            setSortBy({
                key: column.key,
                dataType: column.dataType,
                desc: true
            })
        } else {
            if (sortBy.desc) {
                setSortBy({ key: sortBy.key, dataType: column.dataType, desc: false })
            } else {
                setSortBy(null)
            }
        }
    }

    const { className, displayHeader, columns, multiselect, select, selected } = props

    const onMultiSelect = (list) => {
        const newSelected = {}
        if (list.length !== Object.keys(selected).length) {
            list.forEach(item => {
                newSelected[item[uniqueKey]] = item
            })
        }
        select(newSelected)
    }

    return (
        <div
            className={classNames({
                [classes.wrapper]: true,
                [className]: !!className
            })}
        >
            <div className={classes.tableHeaderWrapper}>
                <Table className={classes.tableHead}>
                    {!(displayHeader === false) && columns.length > 0 && (
                        <TableHead className={classes.headerCellsWrap}>
                            <TableRow
                                className={classNames({
                                    [classes.row]: true,
                                    [classes.headerRow]: true
                                })}
                            >
                                {multiselect &&
                                    <TableCell
                                        className={classNames({
                                            [classes.multiSelectCell]: true,
                                            [classes.headerCell]: true,
                                            [classes.headerMultiSelectCell]: true
                                        })}
                                    >
                                        <Checkbox
                                            onClick={onMultiSelect.bind(this, list)}
                                            checked={list.length === Object.keys(selected).length}
                                            indeterminate={Object.keys(selected).length !== 0 && list.length > Object.keys(selected).length}
                                        />
                                    </TableCell>
                                }
                                {columns.map((column, idx) => (
                                    <TableCell
                                        key={idx}
                                        className={classNames({
                                            [classes.cell]: true,
                                            [classes.headerCell]: true,
                                            [classes.headerCellSortable]: !!column.sortable,
                                            [classes.cellAlignCenter]: column.alignCenter
                                        })}
                                        style={column.style || {}}
                                        onClick={onHeaderColumnClick.bind(this, column)}
                                        headercellcolumnkey={column.key}
                                        columnkey={column.key}
                                    >
                                        <span className={classNames({
                                            [classes.headerLabel]: true,
                                            [classes.headerLabelSortbale]: !!column.sortable
                                        })}>
                                            {column.label}
                                        </span>
                                        {column.sortable &&
                                            sortBy &&
                                            sortBy.key === column.key &&
                                            (sortBy.desc ? <ArrowDownIcon /> : <ArrowUpIcon />)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                    )}
                </Table>
            </div>
            <div className={classes.bodyWrapper}>{body}</div>
        </div>
    )
}

function CustomTableRow(props) {
    const classes = useStyles()
    const { uniqueKey, item, columns, customRenderer, autoHeight, multiselect, select, selected } = props

    const cellValue = (column, item) => {
        const value = (column.key && getObjValueByKey(item, column.key)) || null

        if (column.useEllipsis && !column.disableTooltip) {
            // TODO: fix perfomance issue with tooltip in big list.
            return (
                <CustomTooltip text={value} hover placement="top">
                    {value}
                </CustomTooltip>
            )
        } else {
            return <span>{value}</span>
        }
    }

    const onClick = (ev) => {
        const { onRowClick, item } = props
        if (typeof onRowClick === 'function') {
            onRowClick(ev, item)
        }
    }

    const onSelect = (uniqueKey, item) => {
        const newSelected = cloneDeep(selected)
        if (newSelected.hasOwnProperty(uniqueKey)) {
            delete newSelected[uniqueKey]
        } else {
            newSelected[uniqueKey] = item
        }
        select(newSelected)
    }

    return (
        <TableRow
            className={classNames({
                [classes.row]: true,
                [classes.rowAutoHeight]: !!autoHeight
            })}
            onClick={onClick}
        >
            {multiselect &&
                <TableCell
                    className={classNames({
                        [classes.multiSelectCell]: true
                    })}
                >
                    <Checkbox
                        checked={selected.hasOwnProperty(uniqueKey)}
                        onClick={onSelect.bind(this, uniqueKey, item)}
                    />
                </TableCell>
            }
            {columns.map((column, idx) => {
                const options = {
                    cellData: (column.key && getObjValueByKey(item, column.key)) || null,
                    columnKey: column.key,
                    rowData: item,
                    column
                }
                return (
                    <TableCell
                        key={idx}
                        className={classNames({
                            [classes.cell]: true,
                            [classes.ellipsis]: !!column.useEllipsis,
                            [classes.cellAlignCenter]: column.alignCenter
                        })}
                        cellcolumnkey={column.key}
                        columnkey={column.key}
                        style={column.style || {}}
                    >
                        {column.useCustomRenderer ? customRenderer(options) : cellValue(column, item)}
                    </TableCell>
                )
            })}
        </TableRow>
    )
}

/*
AblTable.propTypes = {
    data: PropTypes.array, // array of objects
    dataUniqueKey: PropTypes.string, // default: 'id'. unique key of data object used for list render optimization.
    displayHeader: PropTypes.bool, // default: true.
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.string, // key of data object. could be `null` if you want to render smth different.
            label: PropTypes.any, // header label - could be a string, component or whatever.
            useCustomRenderer: PropTypes.bool, // default: false. use it in case you want to render data in a custom way.
            sortable: PropTypes.bool,
            dataType: PropTypes.string, // default: string. should be specified if u want to help sort be more precise.
            useEllipsis: PropTypes.bool // ellipsis will be applied on cell.
        })
    )
    customRenderer: PropTypes.func, // (idx, key, data) => (React element)
    className: PropTypes.any,
    rowAutoHeight: PropTypes.bool, // default: false. set row height to auto.
    wrapBody: PropTypes.func // (body) => (). used to wrap body in InfiniteScroller (lazy-load).

}
*/

export default CustomTable

/*
    EXAMPLE:

    <AblTable
        data={[{ id: 1, name: 'name', date: '2019-03-07T13:29:25.652Z' }]}
        columns={[
            {
                key: 'id',
                label: 'ID',
                sortable: true
            },
            {
                key: 'name',
                label: 'Name',
                sortable: true,
                useEllipsis: true
            },
            {
                key: 'date',
                label: 'Date',
                dataType: 'date',
                useCustomRenderer: true
            }
        ]}
        customRenderer={(idx, key, data) => {
            if (idx === 2) {
                return moment(data[key]).format('DD.MM.YYYY')
            }
        }}
    />
*/
